var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showErrorToast, showSuccessToast } from '@ui';
import { attachmentsKeys } from './queryKeys';
/**
 * Delete is the one mass action every attachment surface has, so it lives here; category,
 * visibility and any other per-entity bulk action stay in the surface's own config
 * (`renderBulkActions`), which is where their endpoints and copy live.
 *
 * One `pendingDelete` state drives a single `ConfirmDialog` for both the toolbar's bulk delete
 * (E2E-32) and the per-row icon (E2E-33), rather than building two.
 */
export function useAttachmentDeletion({ entityKey, ownerId, destroyOne, destroyMany, clearSelection, labels }) {
    const queryClient = useQueryClient();
    const [pendingDelete, setPendingDelete] = useState(null);
    const invalidate = useCallback(() => {
        void queryClient.invalidateQueries({ queryKey: attachmentsKeys(entityKey).list(ownerId) });
    }, [queryClient, entityKey, ownerId]);
    const deleteMutation = useMutation({
        mutationFn: (target) => __awaiter(this, void 0, void 0, function* () {
            if (target.fileName) {
                yield destroyOne(ownerId, target.ids[0]);
                return;
            }
            yield destroyMany(ownerId, target.ids);
        }),
        onSuccess: (_data, target) => {
            showSuccessToast(labels.successToast(target.ids.length));
            setPendingDelete(null);
            clearSelection();
            invalidate();
        },
        // On failure: toast AND still invalidate, so the UI re-syncs with the server rather than
        // showing a stale state (MassActions.tsx:73-88 precedent).
        onError: () => {
            setPendingDelete(null);
            showErrorToast(labels.errorToast);
            invalidate();
        }
    });
    const requestDelete = useCallback((ids, fileName) => setPendingDelete({ ids, fileName }), []);
    const cancelDelete = useCallback(() => setPendingDelete(null), []);
    const confirmDelete = useCallback(() => {
        if (pendingDelete)
            deleteMutation.mutate(pendingDelete);
    }, [pendingDelete, deleteMutation]);
    return {
        isMutating: deleteMutation.isLoading,
        pendingDelete,
        requestDelete,
        cancelDelete,
        confirmDelete,
        invalidate
    };
}
